import * as fflate from 'fflate';
import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { EventSubscriptions } from '~/domain/events/EventSubscriptions';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { DicomContour } from '../../../domain/dicom/dicomstructureset';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
    RoiContoursModifiedEvent,
    RoiPropertiesUpdatedEvent,
} from '../../../domain/events';
import { ViewManager } from '../../view/ViewManager';
import { VolumeViewUpdateEvent } from '../../view/events/VolumeViewUpdateEvent';
import { Grid } from '../common/Grid';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { Contour } from './Contour';
import { ContourSequence } from './ContourSequence';
import { ContourSlice } from './ContourSlice';
import { ContourTexture } from './ContourTexture';

/**
 * Helper class to manage the region of interest texture modifications.
 */
export class TextureWrite {
    private readonly initialPixels?: Uint8Array[];
    private readonly initialGrid?: Grid;

    constructor(
        public readonly roi: RegionOfInterest,
        public readonly operation: string,
        initialPixels?: Uint8Array[],
        initialGrid?: Grid
    ) {
        this.initialPixels = initialPixels;
        this.initialGrid = initialGrid;
    }

    public get texture(): ContourTexture | undefined {
        return this.roi.texture;
    }

    public cancel(): void {
        this.roi.resetModificationFlag();
    }

    public finish() {
        this.roi.markAsChanged();

        const texture = this.roi.texture;
        if (texture) {
            const pixels = texture.pixels?.map((slice) => slice.slice());
            const grid = texture.textureGrid;
            const roiNumber = this.roi.roiNumber;
            const operation = this.operation;
            const initialPixels = this.initialPixels;
            const initialGrid = this.initialGrid;
            if (pixels) {
                setTimeout(() => {
                    const event = new RoiContoursModifiedEvent(
                        roiNumber,
                        operation,
                        pixels,
                        grid,
                        initialPixels,
                        initialGrid
                    );
                    domainEventBus.publish(event).catch((error) => {
                        console.error(
                            `Error publishing contour event: ${error}`
                        );
                    });
                }, 0);
            }
        }
    }
}

export class RegionOfInterest {
    public readonly roiNumber: number;

    private _roiName: string;
    private _color8: DicomColor = { R: 255, G: 0, B: 0 };
    private _color: ReadonlyVec3 = vec3.fromValues(1, 0, 0);
    private _contourThickness = 1;
    private _visible = true;

    private readonly contourSequence: ContourSequence;

    private _allSlicesChanged = false;
    private _modificationStarted = false;

    // Indicates if the ROI has been touched/modified
    private _touched = false;

    private contourTexture?: ContourTexture;

    private _eventSubscriptions?: EventSubscriptions;

    // Segment version tracking (increments whenever contour pixels / texture SDF change)
    private _sdfVersion = 0;
    private _sdfModifiedAt = Date.now(); // epoch ms timestamp of last contour change

    public constructor(
        private parentRoi: DicomRoi,
        public readonly _imageGrid: Grid
    ) {
        this.roiNumber = parentRoi.roiNumber;
        this._roiName = parentRoi.name;
        this._visible = parentRoi.visible;
        this._contourThickness = parentRoi.contourThickness;

        this.colorRgb8 = parentRoi.color;

        const slices = RegionOfInterest.toSlices(
            parentRoi.contours,
            this._imageGrid
        );
        this.contourSequence = new ContourSequence(this._imageGrid, slices);

        const subscriptions = [
            domainEventBus.subscribe(
                RoiPropertiesUpdatedEvent.EVENT_TYPE,
                this.handleRoiPropertiesUpdated.bind(this)
            ),
            domainEventBus.subscribe(
                ContourUndoRequestedEvent.EVENT_TYPE,
                this.handleContourUndoRedoRequested.bind(this)
            ),
            domainEventBus.subscribe(
                ContourRedoRequestedEvent.EVENT_TYPE,
                this.handleContourUndoRedoRequested.bind(this)
            ),
        ];

        this._eventSubscriptions = new EventSubscriptions(subscriptions);
    }

    public get isVisible(): boolean {
        return this._visible;
    }

    public set visibility(visible: boolean) {
        if (this._visible !== visible) {
            this._visible = visible;
        }
    }

    public get contourThickness(): number {
        return this._contourThickness;
    }

    public set contourThickness(contourThickness: number) {
        this._contourThickness = contourThickness;
    }

    public get canModify(): boolean {
        return this.parentRoi.canModify;
    }

    private _ensureCanModify(operation: string): void {
        if (!this.canModify) {
            throw new Error(
                `Cannot modify approved structure '${this.name}'` +
                    ` (operation: ${operation})`
            );
        }
    }

    /** Whether this ROI represents a single point (MARKER, ISOCENTER, REGISTRATION). */
    public get isPointRoi(): boolean {
        return this.parentRoi.isPointRoi;
    }

    /** The point coordinates in mm for point-type ROIs, or null. */
    public get pointCoordinatesMm(): ReadonlyVec3 | null {
        return this.parentRoi.pointCoordinatesMm;
    }

    /**
     * Method to get reference to {@link contourSequence}-data.\
     * This updates the {@link ContourSequence} before returning it, if the data is not up-to-date.
     */
    public get sequence(): ContourSequence {
        if (this.isPointRoi) {
            return this.contourSequence;
        }
        if (!this.isSequenceUpToDate) {
            if (this.contourTexture) {
                this.updateSequence();
            } else {
                throw new Error(
                    'RegionOfInterest get sequence(): texture is undefined'
                );
            }
        }
        return this.contourSequence;
    }

    public get hasBeenModified(): boolean {
        return this._allSlicesChanged;
    }

    public get isSequenceUpToDate(): boolean {
        return this._allSlicesChanged === false;
    }

    private checkModificationFlag() {
        if (this._modificationStarted) {
            console.warn(
                `${this.name} updateSequence(): modifications flag on`
            );
        }
    }

    private updateSequence(): void {
        this.checkModificationFlag();
        this.contourSequence.updateFromContourTexture(this.contourTexture!);
        this._allSlicesChanged = false;
    }

    private _renderErrors?: string = '';
    /**
     * Method to get reference to {@link contourTexture}-data.\
     * This updates the {@link ContourTexture} before returning it, if the data is not up-to-date.
     * Returns undefined for point ROIs which have no SDF texture.
     */
    public get texture(): ContourTexture | undefined {
        if (this.isPointRoi) {
            return undefined;
        }
        if (!this.contourTexture) {
            const { texture, renderErrors } =
                ContourTexture.fromContourSequence(
                    this._imageGrid,
                    this.contourSequence
                );
            this.contourTexture = texture;
            this._renderErrors = renderErrors;
        }
        return this.contourTexture;
    }

    public set texture(texture: ContourTexture | undefined) {
        if (texture) {
            this._ensureCanModify('set texture');
        }
        this.contourTexture = texture;
        if (texture) {
            this.bumpSdfVersion();
        }
    }

    /**
     * Asserts that this ROI has an SDF texture.
     * Point ROIs (MARKER, ISOCENTER, REGISTRATION) have no
     * texture.
     */
    public assertHasTexture(): void {
        if (!this.texture) {
            throw new Error(`ROI '${this.name}' has no SDF texture`);
        }
    }

    public get renderErrors(): string | undefined {
        return this._renderErrors;
    }

    public get color(): ReadonlyVec3 {
        return this._color;
    }

    public get colorRgb8(): DicomColor {
        return this._color8;
    }

    public set colorRgb8(color: DicomColor) {
        this._color8 = color;
        this._color = vec3.fromValues(
            color.R / 255,
            color.G / 255,
            color.B / 255
        );
    }

    public get isEmpty(): boolean {
        if (this.isPointRoi) {
            return this.pointCoordinatesMm === null;
        }
        return this.contourTexture?.isEmpty ?? true;
    }

    public get volumeCm3(): number {
        if (this.isPointRoi) {
            return 0;
        }
        if (!this.contourTexture) {
            return 0.0;
        }
        return this.contourTexture.volumeCm3;
    }

    /**
     * Method to get the BoundingBox of the structure texture.
     */
    public get boundingBox(): RotatedBoundingBox {
        if (this.isPointRoi) {
            return new RotatedBoundingBox();
        }
        return this.texture?.fullBoundingBox ?? new RotatedBoundingBox();
    }

    /**
     * Handle contour undo/redo requests from domain events
     */
    private handleContourUndoRedoRequested(
        event: ContourUndoRequestedEvent | ContourRedoRequestedEvent
    ) {
        if (event.roiNumber === this.roiNumber) {
            if (!this.canModify) {
                console.warn(
                    `Ignoring undo/redo for approved structure` +
                        ` '${this.name}'`
                );
                return;
            }
            if (this.contourTexture) {
                this.restorePixelsFromCompressed(
                    event.compressedPixels,
                    event.grid
                );
            }

            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    undefined,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    // --- Version bump utility -------------------------------------------------
    private bumpSdfVersion(): void {
        this._sdfVersion++;
        this._sdfModifiedAt = Date.now();
    }

    // --- Public getters for external dependency freshness checks --------------
    public get sdfVersion(): number {
        return this._sdfVersion;
    }

    public get sdfModifiedAt(): number {
        return this._sdfModifiedAt;
    }

    /**
     * Returns immutable revision info for dependency tracking.
     * Consumers can cache version and compare later to know if recompute needed.
     */
    public get contourRevisionInfo(): { version: number; updatedAt: number } {
        return {
            version: this._sdfVersion,
            updatedAt: this._sdfModifiedAt,
        };
    }

    private restorePixelsFromCompressed(
        compressedPixels: Uint8Array,
        grid: Grid
    ) {
        if (!this.contourTexture) {
            return;
        }

        const decompressedPixels = fflate.decompressSync(compressedPixels);
        const pixels = [];
        const sliceSize = grid.sizeX * grid.sizeY;

        for (let z = 0; z < grid.sizeZ; z++) {
            pixels.push(
                decompressedPixels.slice(z * sliceSize, (z + 1) * sliceSize)
            );
        }

        this.contourTexture.resetSdfAndGrid(pixels, grid);
        this.bumpSdfVersion(); // bump when undo/redo changes contour pixels
    }

    public static toSlices(
        dicomContours: DicomContour[],
        imageGrid: Grid
    ): ContourSlice[] {
        const normalDirection = imageGrid.getUnitZ();
        const contourSlices: ContourSlice[] = [];
        for (const dicomContour of dicomContours) {
            if (dicomContour.data.length > 2) {
                // minimum 3 points to create a contour
                const contour = new Contour(dicomContour.data.map(vec3.clone));
                const contourNormalPosition = vec3.dot(
                    contour.vertices[0],
                    normalDirection
                );
                // there may be multiple contours on the same slice
                const existingSlice = contourSlices.find((slice) =>
                    ContourSlice.isSameSlice(
                        slice.normalPosition,
                        contourNormalPosition
                    )
                );
                if (!existingSlice) {
                    contourSlices.push(
                        new ContourSlice(contourNormalPosition, [contour])
                    );
                } else {
                    existingSlice.addContour(contour);
                }
            }
        }

        return contourSlices;
    }

    public startModify(operation: string): TextureWrite {
        this._ensureCanModify(operation);
        if (this.isPointRoi) {
            throw new Error(
                `Cannot modify point ROI '${this.name}' ` +
                    'through SDF pipeline'
            );
        }

        this._modificationStarted = true;

        let initialPixels: Uint8Array[] | undefined;
        let initialGrid: Grid | undefined;

        if (this.texture && !this._touched) {
            initialPixels = this.texture.pixels?.map((slice) => slice.slice());
            initialGrid = this.texture.textureGrid;
        }

        return new TextureWrite(this, operation, initialPixels, initialGrid);
    }

    /**
     * Replaces the ROI texture and records the change for undo/redo.
     * Combines startModify, texture assignment, and finish into
     * a single call for operations that produce a new texture.
     */
    public replaceTexture(texture: ContourTexture, operation: string): void {
        const modify = this.startModify(operation);
        this.texture = texture;
        modify.finish();
    }

    public markAsChanged(): void {
        this._allSlicesChanged = true;
        this._modificationStarted = false;
        this._touched = true;
        this.bumpSdfVersion(); // bump on any modification completion
    }

    /**
     * Reset the modification flag without marking the ROI
     * as changed. Used when a modification is cancelled and
     * the original data has been restored.
     */
    public resetModificationFlag(): void {
        this._modificationStarted = false;
    }

    public get name(): string {
        return this._roiName;
    }

    /**
     * Handle ROI property updates from domain events
     * Updates engine state and triggers viewport updates based on event data
     */
    private handleRoiPropertiesUpdated(event: RoiPropertiesUpdatedEvent): void {
        if (event.roiNumber !== this.roiNumber) {
            return;
        }

        const properties = event.updatedProperties;

        if (properties.visible !== undefined) {
            this._visible = properties.visible;
        }

        if (properties.color !== undefined) {
            this.colorRgb8 = properties.color;
        }

        if (properties.contourThickness !== undefined) {
            this._contourThickness = properties.contourThickness;
        }

        if (properties.name !== undefined) {
            this._roiName = properties.name;
        }

        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.boundingBox,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    /**
     * Method to delete the {@link RegionOfInterest}.\
     * This de-allocates all the WebGL memory reserved by the different data models within this {@link RegionOfInterest}.
     */
    public delete() {
        this.texture?.delete();
        this._eventSubscriptions?.unsubscribeAll();
    }
}
